import { filterClasses } from "@swivl-mono/helpers";
import { MemberInterface, NoteCreatedByType, NoteInterface, noteSyncLabel } from "@swivl-mono/types";
import { IconPin, IconUnpin } from "@swivl-mono/ui";
import { motion } from "framer-motion";
import { useMemo } from "react";

import NotesModel from "../../../../../Models/NotesMode/Notes.Model";

interface UserDetailsNoteProps {
  note: NoteInterface;
  membersMap?: { [memberId: number]: MemberInterface };
}

const formatMemberName = (displayName?: string) => {
  if (!displayName) return "";

  const firstName = displayName.split(" ")[0];
  const lastNameInitial = displayName.split(" ")[1]?.charAt(0) || "";

  if (lastNameInitial) {
    return `${firstName} ${lastNameInitial}.`;
  } else {
    return firstName;
  }
};

const getTime = (date: Date) => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDateMDDYY = (date: Date) => {
  return date.toLocaleDateString([], { month: "2-digit", day: "2-digit", year: "2-digit" });
};

const UserDetailsNote = ({ note, membersMap }: UserDetailsNoteProps) => {
  const { creator, memberId } = note.createdBy || {};
  const { syncStatus } = note;

  const author = useMemo(() => {
    if (creator === NoteCreatedByType.Member) {
      if (!memberId || !membersMap) return "";
      const member = membersMap[memberId];
      if (!member) return "";
      return formatMemberName(member.agentName);
    } else if (creator === NoteCreatedByType.System) {
      return "FMS";
    } else {
      return "Workflow";
    }
  }, [creator, memberId, membersMap]);

  const handleTogglePinNote = () => {
    NotesModel.setPinnedForNote(note.userId, note.id, !note.pinned);
  };

  const noteVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20, scale: 0.95 }
  };

  const noteDate = note.createdAt ? formatDateMDDYY(new Date(note.createdAt)) : note.updatedAt ? formatDateMDDYY(new Date(note.updatedAt)) : "";
  const noteTime = note.createdAt ? getTime(new Date(note.createdAt)) : note.updatedAt ? getTime(new Date(note.updatedAt)) : "";

  return (
    <motion.div
      className={filterClasses(
        "flex flex-col rounded-sm border border-yellow-800 p-3",
        creator === NoteCreatedByType.System ? "bg-white" : "bg-yellow-50"
      )}
      animate="visible"
      initial="hidden"
      exit="exit"
      variants={noteVariants}
    >
      {/* header */}
      <div className="mb-2.5 flex items-center justify-between font-mono text-xs">
        <div className="flex gap-3 text-yellow-800">
          <span className="font-bold">Note</span>
          {noteDate && noteTime && (
            <>
              <span>{noteDate}</span>
              <span>-</span>
              <span>{noteTime}</span>
            </>
          )}
        </div>
        <div>
          <button className="cursor-pointer" onClick={handleTogglePinNote}>
            {note.pinned ? <IconUnpin className="h-[14px] w-3" /> : <IconPin className="h-[14px] w-3" />}
          </button>
        </div>
      </div>

      {/* content */}
      <div className="mb-4">
        <p className="text-sm text-black">{note.content}</p>
      </div>

      {/* footer */}
      <div className="flex items-center justify-between font-mono text-xs text-yellow-800">
        <span>{author}</span>
        <span>{noteSyncLabel(note)}</span>
      </div>
    </motion.div>
  );
};

export default UserDetailsNote;
